import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM, fmtRMSigned } from "@/lib/format";
import { StatusBadge, CollectionStateBadge } from "@/components/StatusBadge";
import { ArrowLeft, User, Loader2, ChevronUp, ChevronDown, ChevronsUpDown, Phone, MapPin, CreditCard, CalendarClock, Pencil, Check, X } from "lucide-react";

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
        <div className="text-sm text-slate-800">{value || "—"}</div>
      </div>
    </div>
  );
}

function CustomerInfoCard({ c, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(c.dueDay ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    const day = parseInt(val, 10);
    if (!day || day < 1 || day > 31) {
      setErr("Enter a day between 1 and 31");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await api.put("/customers/salary-day", { customerName: c.customerName, dueDay: day });
      setEditing(false);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 mb-4" data-testid={`customer-info-card-${c.customerName}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <InfoItem icon={Phone} label="Phone" value={c.altPhone ? `${c.phone || "—"} · alt: ${c.altPhone}` : c.phone} />
        <InfoItem icon={CreditCard} label="IC / Passport" value={c.icPassport} />
        <InfoItem icon={MapPin} label="Address" value={c.address} />
        <div className="flex items-start gap-2.5">
          <CalendarClock className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
              Salary Day
              {c.dueDayManual && <span className="text-[9px] bg-slate-100 text-slate-500 rounded px-1 py-0.5 normal-case font-medium">manual</span>}
            </div>
            {editing ? (
              <div className="mt-1">
                <div className="flex items-center gap-1.5">
                  <input
                    data-testid={`salary-day-input-${c.customerName}`}
                    type="number"
                    min={1}
                    max={31}
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    className="w-16 border border-slate-300 rounded-md px-2 py-1 text-sm font-mono-fig focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  />
                  <button data-testid={`salary-day-save-${c.customerName}`} onClick={save} disabled={saving} className="bg-slate-900 text-white rounded-md p-1.5 disabled:opacity-50">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  </button>
                  <button onClick={() => { setEditing(false); setVal(c.dueDay ?? ""); setErr(""); }} className="border border-slate-300 rounded-md p-1.5"><X className="h-3.5 w-3.5" /></button>
                </div>
                {err && <div className="text-xs text-red-600 mt-1">{err}</div>}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-800 font-mono-fig">{c.dueDay ? `Day ${c.dueDay}` : "—"}</span>
                <button data-testid={`salary-day-edit-${c.customerName}`} onClick={() => setEditing(true)} className="text-slate-400 hover:text-slate-900"><Pencil className="h-3 w-3" /></button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1.5 font-mono-fig text-xl font-bold ${accent || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

const SORT_ACCESSORS = {
  code: (l) => l.code || "",
  dateApplied: (l) => l.dateApplied || "",
  loanApply: (l) => Number(l.loanApply) || 0,
  incomeCollected: (l) => Number(l.incomeCollected) || 0,
  outstanding: (l) => Number(l.outstanding) || 0,
  actualProfitLoss: (l) => Number(l.actualProfitLoss) || 0,
  status: (l) => l.status || "",
};

function sortLoans(loans, key, dir) {
  const acc = SORT_ACCESSORS[key] || SORT_ACCESSORS.dateApplied;
  const sorted = [...loans].sort((a, b) => {
    const va = acc(a), vb = acc(b);
    if (va < vb) return -1;
    if (va > vb) return 1;
    return 0;
  });
  return dir === "desc" ? sorted.reverse() : sorted;
}

function SortHeader({ label, sortKey, active, dir, onSort, align = "left" }) {
  const Icon = !active ? ChevronsUpDown : dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <th className={`px-3 py-3 font-medium ${align === "right" ? "text-right" : ""}`}>
      <button
        type="button"
        data-testid={`customer-sort-${sortKey}`}
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? "text-slate-900" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </th>
  );
}

export default function CustomerDetail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("dateApplied");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/customers", { params: { q } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [q]);

  const customers = data?.customers || [];

  return (
    <Layout>
      <button
        type="button"
        data-testid="customer-back"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/loans"))}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <PageHeader title="Customer Record" subtitle={`Matching "${q}"`} />

      {loading && (
        <div className="flex items-center gap-2 text-slate-400 py-16 justify-center" data-testid="customer-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      )}

      {!loading && customers.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-400" data-testid="customer-empty">
          No customer found matching "{q}".
        </div>
      )}

      <div className="space-y-8">
        {customers.map((c) => (
          <div key={c.customerName} data-testid={`customer-block-${c.customerName}`}>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="h-9 w-9 rounded-md bg-slate-900 flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-heading font-bold text-slate-900 text-lg leading-tight">{c.customerName}</div>
                <div className="text-xs text-slate-400">{c.totals.loanCount} loan(s) on record</div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
              <Stat label="Total Loan Apply" value={fmtRM(c.totals.totalLoanApply)} />
              <Stat label="Total Disbursed" value={fmtRM(c.totals.totalDisbursed)} />
              <Stat label="Total Income" value={fmtRM(c.totals.totalIncome)} accent="text-green-600" />
              <Stat label="Total Collected" value={fmtRM(c.totals.totalCollected)} />
              <Stat label="Total Outstanding" value={fmtRM(c.totals.totalOutstanding)} accent="text-amber-600" />
              <Stat
                label="Total Profit / Loss"
                value={fmtRMSigned(c.totals.totalProfitLoss)}
                accent={c.totals.totalProfitLoss >= 0 ? "text-green-600" : "text-red-600"}
              />
            </div>

            <CustomerInfoCard c={c} onSaved={load} />

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
              <table className="w-full text-sm" data-testid={`customer-loans-${c.customerName}`}>
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                    <SortHeader label="Code" sortKey="code" active={sortKey === "code"} dir={sortDir} onSort={handleSort} />
                    <SortHeader label="Date" sortKey="dateApplied" active={sortKey === "dateApplied"} dir={sortDir} onSort={handleSort} />
                    <th className="px-3 py-3 font-medium">Formula</th>
                    <SortHeader label="Loan Apply" sortKey="loanApply" active={sortKey === "loanApply"} dir={sortDir} onSort={handleSort} align="right" />
                    <SortHeader label="Income" sortKey="incomeCollected" active={sortKey === "incomeCollected"} dir={sortDir} onSort={handleSort} align="right" />
                    <SortHeader label="Outstanding" sortKey="outstanding" active={sortKey === "outstanding"} dir={sortDir} onSort={handleSort} align="right" />
                    <SortHeader label="Profit / Loss" sortKey="actualProfitLoss" active={sortKey === "actualProfitLoss"} dir={sortDir} onSort={handleSort} align="right" />
                    <th className="px-3 py-3 font-medium">Collection</th>
                    <SortHeader label="Status" sortKey="status" active={sortKey === "status"} dir={sortDir} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {sortLoans(c.loans, sortKey, sortDir).map((l) => (
                    <tr key={l.code} data-testid={`customer-loan-row-${l.code}`} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-5 py-3 font-mono-fig font-medium">
                        <Link to={`/loans/${l.code}`} className="text-slate-900 hover:underline">{l.code}</Link>
                      </td>
                      <td className="px-3 py-3 text-slate-500 whitespace-nowrap">{l.dateApplied || "—"}</td>
                      <td className="px-3 py-3 font-mono-fig text-xs text-slate-500">{l.repaymentFormula || "—"}</td>
                      <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(l.loanApply)}</td>
                      <td className="px-3 py-3 text-right font-mono-fig text-green-600">{fmtRM(l.incomeCollected)}</td>
                      <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(l.outstanding)}</td>
                      <td className={`px-3 py-3 text-right font-mono-fig font-semibold ${l.actualProfitLoss >= 0 ? "text-green-600" : "text-red-600"}`}>{fmtRMSigned(l.actualProfitLoss)}</td>
                      <td className="px-3 py-3"><CollectionStateBadge state={l.collectionState} /></td>
                      <td className="px-3 py-3"><StatusBadge status={l.status} inactive={l.inactive} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-semibold text-slate-900">
                    <td className="px-5 py-3" colSpan={3}>Total</td>
                    <td className="px-3 py-3 text-right font-mono-fig">{fmtRM(c.totals.totalLoanApply)}</td>
                    <td className="px-3 py-3 text-right font-mono-fig text-green-600">{fmtRM(c.totals.totalIncome)}</td>
                    <td className="px-3 py-3 text-right font-mono-fig">{fmtRM(c.totals.totalOutstanding)}</td>
                    <td className={`px-3 py-3 text-right font-mono-fig ${c.totals.totalProfitLoss >= 0 ? "text-green-600" : "text-red-600"}`}>{fmtRMSigned(c.totals.totalProfitLoss)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
